import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { TagTypeResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetTagsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcTagToGraphQL(
  tagType: TagTypeResponseRow
): GetTagsQuery['tag_type'][number] {
  return {
    ...tagType,
  };
}

/**
 * Maps TRPC control data to match the GraphQL query structure
 */
function mapTrpcTagsToGraphQL(
  trpcData: TagTypeResponseRow[] | undefined
): GetTagsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    tag_type: trpcData.map((tag) => mapTrpcTagToGraphQL(tag)),
  };
}

export const useGetTags = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.tag.allTypes.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcTagsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
